import { loginFromLiff, pauseLiffLogin } from '../lib/liff';
import { createContext, type ComponentChildren } from 'preact';
import { useCallback, useContext, useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import type { Me } from '@shared/types';
import { currentPath, get, patch, post, setPolicyRequiredHandler, setUnauthorizedHandler } from '../api';

interface RequireOptions {
  /** also require a completed profile (default true — join/create actions need it) */
  onboarded?: boolean;
}

export interface AuthApi {
  me: Me | null;
  loginError: string | null;
  loading: boolean;
  refresh: () => Promise<Me | null>;
  logout: () => Promise<void>;
  /** where the login sheet is currently open for, or null */
  loginReturnTo: string | null;
  openLogin: (returnTo?: string) => void;
  closeLogin: () => void;
  /**
   * Runs `action` when signed in (and onboarded, unless disabled).
   * Otherwise opens the login sheet, or routes to onboarding, with return_to set.
   */
  requireUser: (action: () => void | Promise<void>, opts?: RequireOptions) => void;
  /** keep unread count etc. in sync without a refetch */
  patchMe: (p: Partial<Me>) => void;
  /** PATCH /api/me { accept_policy: true } — records acceptance of the current Privacy Policy / Terms version. */
  acceptPolicy: () => Promise<Me | null>;
}

const Ctx = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ComponentChildren }) {
  const { route } = useLocation();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [loginReturnTo, setLoginReturnTo] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await get<{ user: Me | null }>('/api/me', { silent401: true });
      setMe(r?.user ?? null);
      return r?.user ?? null;
    } catch {
      setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      try { const destination = await loginFromLiff(); if (destination) route(destination, true); }
      catch (e) { setLoginError(e instanceof Error ? e.message : 'เชื่อมต่อ LINE ไม่สำเร็จ'); setLoginReturnTo('/'); }
      finally { await refresh(); }
    })();
  }, [refresh]);

  const openLogin = useCallback((returnTo?: string) => setLoginReturnTo(returnTo ?? currentPath()), []);
  const closeLogin = useCallback(() => setLoginReturnTo(null), []);

  useEffect(() => {
    setUnauthorizedHandler((rt) => {
      setMe(null);
      setLoginReturnTo(rt);
    });
    // A 403 `reason: 'policy'` means our cached `me` is stale (or a new version shipped) — refetch so the gate shows.
    setPolicyRequiredHandler(() => {
      setMe((m) => (m ? { ...m, policy_accepted: false } : m));
      void refresh();
    });
    return () => {
      setUnauthorizedHandler(null);
      setPolicyRequiredHandler(null);
    };
  }, [refresh]);

  useEffect(() => {
    if (!me || me.role === 'banned' || me.role === 'deleted') return;
    let pending = false;
    const controller = new AbortController();
    const beat = async () => {
      if (document.visibilityState !== 'visible' || pending) return;
      pending = true;
      try {
        await fetch('/api/members/presence', {method:'POST',credentials:'same-origin',signal:controller.signal});
        const response = await fetch('/api/me', {credentials:'same-origin',signal:controller.signal});
        if (response.ok) {
          const result = await response.json() as {user: Me | null};
          if (!controller.signal.aborted && result.user?.id === me.id) setMe(current => current && current.id === result.user?.id ? {...current, unread_notifications: result.user!.unread_notifications} : current);
        }
      } catch {} finally { pending=false; }
    };
    void beat();
    const timer=setInterval(beat,30000);
    document.addEventListener('visibilitychange',beat);
    return () => { clearInterval(timer); controller.abort(); document.removeEventListener('visibilitychange',beat); };
  }, [me?.id, me?.role]);

  const acceptPolicy = useCallback(async () => {
    const r = await patch<{ user: Me | null }>('/api/me', { accept_policy: true });
    const m = r?.user ?? null;
    if (m) setMe(m);
    return m;
  }, []);

  const logout = useCallback(async () => {
    try {
      await post('/api/auth/logout');
      pauseLiffLogin();
    } finally {
      setMe(null);
      route('/');
    }
  }, [route]);

  const requireUser = useCallback<AuthApi['requireUser']>(
    (action, opts) => {
      if (loading) return;
      if (!me) return openLogin();
      if (me.role === 'banned' || me.role === 'deleted') return;
      if ((opts?.onboarded ?? true) && !me.onboarded) {
        route(`/me/onboarding?return_to=${encodeURIComponent(currentPath())}`);
        return;
      }
      void action();
    },
    [loading, me, openLogin, route],
  );

  const patchMe = useCallback((p: Partial<Me>) => setMe((m) => (m ? { ...m, ...p } : m)), []);

  const value = useMemo<AuthApi>(
    () => ({ me, loginError, loading, refresh, logout, loginReturnTo, openLogin, closeLogin, requireUser, patchMe, acceptPolicy }),
    [me, loginError, loading, refresh, logout, loginReturnTo, openLogin, closeLogin, requireUser, patchMe, acceptPolicy],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
